/**
 * Context management for logging
 * Handles accumulation and management of logging context
 */

import type { LogContext } from './types';

export class ContextManager {
  private globalContext: LogContext = {};
  private scopedContextStack: LogContext[] = [];

  /**
   * Adds context that will be included in all subsequent logs
   */
  addGlobalContext(context: LogContext): void {
    this.globalContext = { ...this.globalContext, ...context };
  }

  /**
   * Clears specific keys from global context
   */
  clearGlobalContext(...keys: string[]): void {
    if (keys.length === 0) {
      this.globalContext = {};
    } else {
      keys.forEach((key) => {
        delete this.globalContext[key];
      });
    }
  }

  /**
   * Push a new scoped context onto the stack
   */
  pushScope(context: LogContext): void {
    this.scopedContextStack.push(context);
  }

  /**
   * Pop the most recent scoped context
   */
  popScope(): LogContext | undefined {
    return this.scopedContextStack.pop();
  }

  /**
   * Get the current combined context
   */
  getCurrentContext(): LogContext {
    // Merge all contexts, with more recent scopes overriding older ones
    const result = { ...this.globalContext };
    for (const scope of this.scopedContextStack) {
      Object.assign(result, scope);
    }
    return result;
  }

  /**
   * Execute a function with additional scoped context
   */
  withContext<T>(context: LogContext, fn: () => T): T {
    this.pushScope(context);
    try {
      return fn();
    } finally {
      this.popScope();
    }
  }

  /**
   * Create a child context manager with inherited context
   */
  createChild(): ContextManager {
    const child = new ContextManager();
    child.globalContext = { ...this.globalContext };
    child.scopedContextStack = [...this.scopedContextStack];
    return child;
  }

  /**
   * Reset all context
   */
  reset(): void {
    this.globalContext = {};
    this.scopedContextStack = [];
  }
}
